import React, { useState } from "react"
import {
  Box,
  NumberDecrementStepper, NumberIncrementStepper, NumberInput,
  NumberInputField, NumberInputStepper, Text,
} from "@chakra-ui/react"
import { Combobox } from "components/Combobox"

const applyFooterStyle = (isDirty: boolean) => ({
  // role="button" because Apply commits, not a listbox option.
  role: "button" as const,
  "aria-disabled": !isDirty,
  py: 2,
  px: 0,
  bg: "transparent",
  borderTopWidth: 1,
  borderColor: "gray.200",
  justifyContent: "center" as const,
  cursor: isDirty ? "pointer" : "not-allowed",
  transition: "background-color 0.2s ease",
  // sx (not _hover/_active) so :active follows :hover in source order
  // and wins the same-specificity tie on click+hold.
  ...(isDirty && {
    sx: {
      "&:hover": { bg: "blue.50" },
      "&:active": { bg: "blue.100" },
    },
  }),
})

type Props = {
  // undefined = add mode (Apply always armed); string = edit mode
  // (Apply only fires when draft differs from initial).
  initialValue?: string
  onCommit: (values: string[]) => void
}

const NumberPicker = ({ initialValue, onCommit }: Props) => {
  const parsed = initialValue !== undefined ? (Number(initialValue) || 0) : 0
  const [draft, setDraft] = useState<number>(parsed)
  const isEdit = initialValue !== undefined
  const isDirty = !isEdit || draft !== parsed

  const apply = () => {
    if (!isDirty) return
    onCommit([String(draft)])
  }

  return (
    <Box>
      <Box p={3}>
        <NumberInput
          min={0}
          value={draft}
          onChange={(_, val) => setDraft(Number.isFinite(val) ? val : 0)}
          size="sm"
          onKeyDown={(e) => { if (e.key === "Enter") apply() }}
        >
          {/* autoFocus on the field, not on NumberInput — the latter
              doesn't propagate reliably inside a freshly-opened popover. */}
          <NumberInputField fontSize="sm" px={2} autoFocus />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </Box>
      <Combobox.Action onSelect={apply} closeOnSelect={isDirty} {...applyFooterStyle(isDirty)}>
        <Text color="blue.500" fontWeight="semibold" opacity={isDirty ? 1 : 0.4}>
          Apply
        </Text>
      </Combobox.Action>
    </Box>
  )
}

export default NumberPicker
